// JS bridge to the native CallKit plugin (ios/App/App/CallKitPlugin.swift):
// calls ring and show like regular iPhone calls (lock screen, banner, Recents,
// Bluetooth/CarPlay buttons). An incoming call reaches the phone as a PushKit
// VoIP push, so it rings even when the app was killed.
//
// Ids are lowercase UUID strings: the server's callId for incoming calls, an
// id native generates for outgoing ones.
//
// iOS only. Elsewhere (and on an app build without the plugin) everything
// resolves to "not handled" and the in-app call screen and ringtone are used.

import { Capacitor, registerPlugin } from '@capacitor/core';

const CallKit = registerPlugin('CallKit');

export const usesCallKit = () => Capacitor.getPlatform() === 'ios';

const safe = async (fn, fallback) => {
  if (!usesCallKit()) return fallback;
  try {
    return (await fn()) ?? fallback;
  } catch (err) {
    console.warn('[CallKit]', err);
    return fallback;
  }
};

/** Show the system incoming call screen. Resolves false if iOS refused. */
export const reportIncomingCall = ({ id, name, handle, video }) =>
  safe(() => CallKit.reportIncoming({ id, name, handle, video: !!video }), {})
    .then((res) => !!res.reported);

/** Register an outgoing call. Resolves its CallKit id, or null. */
export const startCallKitCall = ({ name, handle, video }) =>
  safe(() => CallKit.startCall({ name, handle, video: !!video }), {})
    .then((res) => res.id || null);

export const reportCallConnecting = (id) => (id ? safe(() => CallKit.reportConnecting({ id })) : null);

export const reportCallConnected = (id) => (id ? safe(() => CallKit.reportConnected({ id })) : null);

/** Answered in the app: CallKit then activates the call audio. */
export const answerCallKitCall = (id) => safe(() => CallKit.answer({ id }));

export const setCallKitMuted = (id, muted) => (id ? safe(() => CallKit.setMuted({ id, muted })) : null);

/**
 * Dismiss the system call UI and release call audio. Call on every call end,
 * with or without an id.
 * reason: remoteEnded | unanswered | answeredElsewhere | declinedElsewhere | failed
 */
export const endCallKitCall = (id, reason = 'remoteEnded') =>
  safe(() => CallKit.endCall({ id, reason }));

/**
 * What the user did on the system call screen: incoming, answer, end, mute.
 * Events from before the app loaded are delivered to the first listener.
 * Returns an unsubscribe fn.
 */
export const onCallKitEvent = (event, callback) => {
  if (!usesCallKit()) return () => {};
  const handle = CallKit.addListener(event, callback);
  return () => {
    Promise.resolve(handle).then((h) => h?.remove()).catch(() => {});
  };
};

/**
 * Send the PushKit token (now and whenever it changes) to `register`.
 * Returns an unsubscribe fn.
 */
export const watchVoipToken = (register) => {
  if (!usesCallKit()) return () => {};
  safe(() => CallKit.getVoipToken(), {}).then((res) => res.token && register(res.token));
  return onCallKitEvent('voipToken', ({ token }) => token && register(token));
};
